import { useQuery, useReactiveVar } from '@apollo/client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  CheckboxInputStyle,
  UncontrolledRadioInput,
} from '@src/components/Inputs'
import { SelectWithArrow } from '@src/components/ServicePopover/SelectWithArrow'
import {
  OptionContentLabels,
  OptionContentSubTitle,
  OptionContentText,
  OptionContentTitle,
  OptionLabel,
  PopoverOptionContainer,
} from '@src/components/BottomSheet/BottomSheet.styles'
import { breakpoints } from '@src/constants/breakpoints'
import { LocationType } from '@src/graphql-types'
import { useLazyCustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { useMarketplaceDeliveryZonesLazyQuery } from '@src/hooks/sharedQueries/useMarketplaceDeliveryZones/useMarketplaceDeliveryZones'
import { useFindLocation } from '@src/hooks/useFindLocation'
import { FulfilmentFilter } from '@src/hooks/useFulfilmentFilter/validation'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { jwtVar } from '@src/models/customer/jwt'
import { screenResolutionVar } from '@src/models/screenResolution'

import { AddressesPopover } from './AddressesPopover'
import { DeliveryLocationSectionContainer } from './DeliveryLocationSection.styles'
import { PostcodePopover } from './PostcodePopover'
import { PlaceSearchPopover } from './PlaceSearchPopover'
import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useBottomSheetStack } from '../../hooks/useBottomSheetStack/useBottomSheetStack'
import { ZonesPopover } from './ZonesPopover'
import { LocationOrderabilityDocument } from './queries/__generated__/LocationOrderability.graphql-interface'

export const DeliveryLocationSection: React.FC<{
  availableLocationTypes: LocationType[]
  basketOutletId?: string | null
  selectedLocation: FulfilmentFilter['where']['location']
  setSelectedLocation: (location: FulfilmentFilter['where']['location']) => void
  historicalData: FulfilmentFilter['where']['historicalData']
  showSubtitles?: boolean
  showSelection?: boolean
  isPopover?: boolean
  handleCancelWhenNavigatingAway: () => void
}> = ({
  availableLocationTypes,
  selectedLocation,
  setSelectedLocation,
  historicalData,
  showSubtitles = true,
  // if false, the dynamic data (eg address / delivery zone name) won't be shown
  showSelection = true,
  isPopover = false,
  handleCancelWhenNavigatingAway,
  basketOutletId,
}) => {
  const isUserLoggedIn = Boolean(useReactiveVar(jwtVar))
  const { t } = useTranslation('serviceNavigation')
  const { outletId } = useParams<{ outletId?: string }>()
  const {
    postcodeBottomSheetController,
    addressPickerBottomSheetController,
    placeSearchBottomSheetController,
    deliveryZoneBottomSheetController,
  } = useBottomSheetStack()

  const checkoutRouter = useCheckoutRouter()

  const outletOrBasketOutletId = (outletId ?? basketOutletId) || undefined

  const locationOrderablityQueryResponse = useQuery(
    LocationOrderabilityDocument,
    outletOrBasketOutletId
      ? {
          variables: {
            outletId: outletOrBasketOutletId,
            fulfilmentLocation: selectedLocation,
          },
        }
      : {
          skip: true,
        }
  )

  const [findingLocation, setFindingLocation] = useState(false)

  const findLocation = useFindLocation({
    onSuccess: coordinates => {
      setSelectedLocation({
        type: LocationType.COORDINATES,
        coordinates,
      })
      setFindingLocation(false)
    },
    onError: () => setFindingLocation(false),
  })

  const marketplace = useMarketplace()
  const [fetchAddresses, { data: addressesData }] =
    useLazyCustomerDetailsAndAddressesQuery({
      addressAcceptingOrdersToOutletId: outletOrBasketOutletId,
    })
  const [fetchDeliveryZones, { data: deliveryZonesData }] =
    useMarketplaceDeliveryZonesLazyQuery()
  const screenResolution = useReactiveVar(screenResolutionVar)

  // fetch data for available locations which display dynamic data (eg address / delivery zone name)
  useEffect(() => {
    if (
      availableLocationTypes.includes(LocationType.ADDRESS) &&
      isUserLoggedIn &&
      !addressesData
    ) {
      void fetchAddresses({ variables: { outletId: outletOrBasketOutletId } })
    }
    if (
      availableLocationTypes.includes(LocationType.DELIVERY_ZONE) &&
      !deliveryZonesData
    ) {
      void fetchDeliveryZones()
    }
  }, [
    outletId,
    showSelection,
    availableLocationTypes,
    isUserLoggedIn,
    addressesData,
    fetchAddresses,
    deliveryZonesData,
    fetchDeliveryZones,
  ])

  const selectedAddress = useMemo(
    () =>
      (addressesData?.customerDetails?.deliveryAddresses || []).find(
        address => address.id === historicalData.addressId
      ),
    [addressesData, historicalData.addressId]
  )

  // takes a location type and returns the data used to populate an <option> within the <select>
  const getOptionDataForLocationType = useCallback(
    (
      locationType: LocationType
    ): {
      value: LocationType
      title: string
      subTitle: string
      onSelect?: () => void
      errorText?: string
    } | null => {
      switch (locationType) {
        case LocationType.EVERYWHERE:
          return {
            value: LocationType.EVERYWHERE,
            title: t('everywhere_option_title'),
            subTitle: t('everywhere_option_subtitle', {
              urlPath: marketplace.urlPath,
            }),
            onSelect: () => {
              setSelectedLocation({
                type: LocationType.EVERYWHERE,
              })
            },
          }
        case LocationType.POSTCODE: {
          return {
            value: LocationType.POSTCODE,
            title:
              showSelection && historicalData.postAndCountryCode?.postcode
                ? historicalData.postAndCountryCode.postcode
                : t('postcode_option_title'),
            subTitle: t('postcode_option_subtitle'),
            onSelect: () => {
              postcodeBottomSheetController.open()
              // TODO prevent closing of basket modal by implementing better controlled focus scope
              if (screenResolution.width < breakpoints.wideDesktop)
                checkoutRouter.reset()
            },
            errorText: t('delivery_not_available', {
              locationType: t('postcode').toLowerCase(),
            }),
          }
        }
        case LocationType.COORDINATES:
          return {
            value: LocationType.COORDINATES,
            title: t('location_option_title'),
            subTitle: t('location_option_subtitle'),
            onSelect: () => {
              setFindingLocation(true)
              findLocation()
            },
            errorText: t('delivery_not_available', {
              locationType: t('location').toLowerCase(),
            }),
          }
        case LocationType.ADDRESS: {
          // if an address is or was selected, find the address
          let title = t('addresses_option_title')
          if (showSelection && historicalData.addressId) {
            title = selectedAddress?.name || title
          }
          return {
            value: LocationType.ADDRESS,
            title,
            subTitle: t('addresses_option_subtitle'),
            onSelect: () => {
              addressPickerBottomSheetController.open()
            },
            errorText: t('delivery_not_available', {
              locationType: t('address').toLowerCase(),
            }),
          }
        }
        case LocationType.DELIVERY_ZONE: {
          let title = t('delivery_zone_option_title')
          if (showSelection && historicalData.zoneId) {
            const selectedZone = (
              deliveryZonesData?.deliveryZonesByMarketplaceId || []
            ).find(zone => zone.id === historicalData.zoneId)
            title = selectedZone?.name || title
          }
          return {
            value: LocationType.DELIVERY_ZONE,
            title,
            subTitle: t('delivery_zone_option_subtitle'),
            onSelect: () => {
              deliveryZoneBottomSheetController.open()
            },
          }
        }
        case LocationType.PLACE_SEARCH: {
          const title: string =
            (showSelection &&
              selectedLocation.type === LocationType.PLACE_SEARCH &&
              selectedLocation.address) ||
            t('place_search_option_title')

          return {
            value: LocationType.PLACE_SEARCH,
            title,
            subTitle: t('place_search_option_subtitle'),
            onSelect: () => {
              placeSearchBottomSheetController.open()
              if (screenResolution.width < breakpoints.wideDesktop)
                checkoutRouter.reset()
            },
          }
        }
      }
    },
    [
      t,
      marketplace.urlPath,
      setSelectedLocation,
      showSelection,
      historicalData.postAndCountryCode,
      historicalData.addressId,
      historicalData.zoneId,
      findLocation,
      addressesData?.customerDetails?.deliveryAddresses,
      deliveryZonesData?.deliveryZonesByMarketplaceId,
    ]
  )

  return (
    <DeliveryLocationSectionContainer>
      {availableLocationTypes.map(locationType => {
        const locationOptionData = getOptionDataForLocationType(locationType)
        // unexpected location option data
        if (locationOptionData === null) return null
        const errorText =
          !locationOrderablityQueryResponse.loading &&
          locationOrderablityQueryResponse.data?.orderabilityByOutletId
            ?.delivery?.isAvailableToLocation === false &&
          locationType === selectedLocation.type
            ? locationOptionData.errorText
            : undefined

        return (
          <PopoverOptionContainer key={locationType}>
            <UncontrolledRadioInput
              label={
                <>
                  <OptionLabel>
                    <OptionContentLabels>
                      {showSubtitles && !errorText ? (
                        <>
                          <OptionContentTitle
                            data-test-id={`location-option-${locationOptionData.title}`}
                          >
                            {locationOptionData.title}
                          </OptionContentTitle>
                          <OptionContentSubTitle>
                            {locationOptionData.subTitle}
                          </OptionContentSubTitle>
                        </>
                      ) : (
                        <OptionContentText
                          data-test-id={`location-option-${locationOptionData.title}`}
                        >
                          {locationOptionData.title}
                        </OptionContentText>
                      )}
                    </OptionContentLabels>
                    {locationOptionData.value === LocationType.ADDRESS && (
                      <SelectWithArrow
                        id={locationOptionData.value}
                        dataPresent={Boolean(historicalData.addressId)}
                        justArrow={
                          !isPopover &&
                          screenResolution.width >= breakpoints.largeTablet
                        }
                      />
                    )}
                    {locationOptionData.value === LocationType.POSTCODE && (
                      <SelectWithArrow
                        id={locationOptionData.value}
                        dataPresent={Boolean(historicalData.postAndCountryCode)}
                        justArrow={
                          !isPopover &&
                          screenResolution.width >= breakpoints.largeTablet
                        }
                      />
                    )}
                    {locationOptionData.value === LocationType.PLACE_SEARCH && (
                      <SelectWithArrow
                        id={locationOptionData.value}
                        dataPresent={Boolean(
                          selectedLocation.type === LocationType.PLACE_SEARCH &&
                            selectedLocation.address
                        )}
                        justArrow={
                          !isPopover &&
                          screenResolution.width >= breakpoints.largeTablet
                        }
                      />
                    )}
                  </OptionLabel>
                </>
              }
              meta={{
                touched: true,
                error: errorText,
              }}
              style={CheckboxInputStyle.TICK}
              checked={selectedLocation.type === locationOptionData.value}
              onClick={locationOptionData.onSelect}
              loading={
                locationType === LocationType.COORDINATES
                  ? findingLocation
                  : false
              }
            />
          </PopoverOptionContainer>
        )
      })}

      {deliveryZonesData && (
        <ZonesPopover
          onDone={zoneId => {
            setSelectedLocation({
              type: LocationType.DELIVERY_ZONE,
              zoneId,
            })
          }}
          selectedZoneId={historicalData.zoneId}
          availableZones={deliveryZonesData.deliveryZonesByMarketplaceId}
        />
      )}

      <PostcodePopover
        onDone={postcode => {
          setSelectedLocation({
            type: LocationType.POSTCODE,
            postAndCountryCode: {
              postcode,
              countryCode: marketplace.country.ISO3166Alpha2,
            },
          })
        }}
        postcode={historicalData.postAndCountryCode?.postcode}
        isPopover={isPopover}
      />

      <PlaceSearchPopover
        onDone={place => {
          setSelectedLocation({
            type: LocationType.PLACE_SEARCH,
            ...place,
          })
        }}
        isPopover={isPopover}
      />

      {addressesData && (
        <AddressesPopover
          onDone={addressId => {
            setSelectedLocation({
              type: LocationType.ADDRESS,
              addressId,
            })
          }}
          selectedAddressId={historicalData.addressId}
          addresses={addressesData.customerDetails.deliveryAddresses}
          handleCancelWhenNavigatingAway={handleCancelWhenNavigatingAway}
          isPopover={isPopover}
        />
      )}
    </DeliveryLocationSectionContainer>
  )
}
